/**
 * Copyright (c) 2026 Stefan Radig
 *
 * Requests to the Android app's native bridges (MainActivity.kt / NativeBridges.kt).
 * The app injects EditoorLocation, EditoorShortcuts and EditoorDeepLink with
 * WebViewCompat.addWebMessageListener; each answers a request
 * `{ id, method, args }` with `{ id, result }` or `{ id, error }`, and only for
 * the main frame of a trusted server (the bundled start page for the deep-link
 * pickup). Anything else gets `error: "not-allowed"`.
 *
 * Requests go through the top window's object when it is reachable: plugin
 * iframes share the shell's origin, and the app only answers the main frame.
 */

export type NativeObjectName = 'EditoorLocation' | 'EditoorShortcuts' | 'EditoorDeepLink' | 'EditoorBoatData';

export const NATIVE_REQUEST_TIMEOUT_MS = 10_000;

/** `not-allowed` (refused by the app), `timeout`, `unavailable` (no bridge), or the app's own error. */
export class NativeRequestError extends Error {
    constructor(readonly code: string) {
        super(`Native request failed: ${code}`);
        this.name = 'NativeRequestError';
    }
}

interface InjectedObject {
    postMessage(message: string): void;
    addEventListener(type: 'message', listener: (event: { data: unknown }) => void): void;
    removeEventListener(type: 'message', listener: (event: { data: unknown }) => void): void;
}

function injectedObject(name: NativeObjectName): InjectedObject | undefined {
    try {
        const top = window.top as unknown as Record<string, InjectedObject | undefined> | null | undefined;
        if (top && top !== (window as unknown)) {
            const object = top[name];
            if (object) return object;
        }
    } catch {
        // the top window is another origin: only this frame's own object is left
    }
    return (window as unknown as Record<string, InjectedObject | undefined>)[name];
}

/** True inside the Android app (the object is injected into every frame). */
export function hasNativeObject(name: NativeObjectName): boolean {
    return injectedObject(name) !== undefined;
}

let counter = 0;

function nextId(): string {
    counter += 1;
    // Unique across frames sharing the top window's object; not a secret.
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${counter}`;
}

/**
 * Sends one request and resolves with its result; rejects with a NativeRequestError.
 * `timeoutMs` is longer for requests that wait on the user or the radio (permission
 * dialog, Bluetooth scan).
 */
export function nativeRequest(
    objectName: NativeObjectName,
    method: string,
    args: unknown[] = [],
    timeoutMs: number = NATIVE_REQUEST_TIMEOUT_MS
): Promise<unknown> {
    const object = injectedObject(objectName);
    if (!object) return Promise.reject(new NativeRequestError('unavailable'));
    const id = nextId();
    return new Promise((resolve, reject) => {
        const finish = () => {
            clearTimeout(timer);
            object.removeEventListener('message', onMessage);
        };
        const onMessage = (event: { data: unknown }) => {
            if (typeof event.data !== 'string') return;
            let reply: { id?: unknown; result?: unknown; error?: unknown };
            try {
                reply = JSON.parse(event.data);
            } catch {
                return;
            }
            if (!reply || reply.id !== id) return;
            finish();
            if (reply.error !== undefined && reply.error !== null) {
                reject(new NativeRequestError(String(reply.error)));
            } else {
                resolve(reply.result ?? null);
            }
        };
        const timer = setTimeout(() => {
            finish();
            reject(new NativeRequestError('timeout'));
        }, timeoutMs);
        object.addEventListener('message', onMessage);
        try {
            object.postMessage(JSON.stringify({ id, method, args }));
        } catch {
            finish();
            reject(new NativeRequestError('unavailable'));
        }
    });
}
